import Ionicons from 'react-native-vector-icons/Ionicons';
import { useNavigation } from '@react-navigation/native';
import { useAuth } from '../context/AuthContext';
import {
    Alert,
    Linking,
    Platform,
    ScrollView,
    StyleSheet,
    Text,
    TouchableOpacity,
    View
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTheme } from '../context/ThemeContext';
import { getThemeColors } from '../utils/themeStyles';
import { useMissions } from '../context/MissionsContext';

function showAlert(title: string, message: string) {
  if (Platform.OS === 'web') {
    window.alert(title + ': ' + message);
  } else {
    Alert.alert(title, message);
  }
}

const STORY_NAME_MAP: Record<number, string> = {
  1: 'EgoCrisis',
  2: 'FoundationGamble',
  3: 'DataStorm',
  109: 'CloudHeist',
  110: 'NeuralOverride',
  111: 'PipelineInferno',
  104: 'QuantumGambit',
  105: 'LearningMachine',
  106: 'InkProtocol',
  108: 'ArabicModern',
  112: 'ArabicModern',
  113: 'ArabicModern',
  114: 'ArabicModern',
  115: 'ArabicModern',
};

function getProgressColor(progress: number): string {
  if (progress >= 80) return '#10B981';
  if (progress >= 40) return '#F59E0B';
  return '#EF4444';
}

export default function DashboardScreen() {
  const navigation = useNavigation<any>();
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);
  const { user } = useAuth();
  const { activeMissions } = useMissions();

  const userName = user
    ? `${user.prenom || ''}${user.nom ? ' ' + user.nom : ''}`.trim() || 'Utilisateur'
    : 'Utilisateur';

  const inProgressMissions = activeMissions.filter(m => m.progress < 100);
  const completedMissions = activeMissions.filter(m => m.progress === 100);

  const handleOpenCertificate = async (missionId: number) => {
    const storyName = STORY_NAME_MAP[missionId] ?? 'EgoCrisis';
    const firstName = user?.prenom ?? 'Amine';
    const lastName = user?.nom ?? 'HAKOUNA';
    const url = `https://ccpubmedia.blob.core.windows.net/certificates/CloudChrono_${storyName}_Certificate_${firstName}_${lastName}.pdf`;
    try {
      const resp = await fetch(url, { method: 'HEAD' });
      if (resp.ok) {
        Linking.openURL(url);
      } else {
        showAlert('Certificat indisponible', 'Le certificat pour cette mission nest pas encore disponible. Reessayez plus tard.');
      }
    } catch {
      showAlert('Erreur', 'Impossible de verifier le certificat. Verifiez votre connexion.');
    }
  };

  const handleMissionPress = (missionId: number) => {
    navigation.navigate('ChapterDetail', { missionId, chapterId: 1 });
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
      <ScrollView
        style={[styles.content, { backgroundColor: colors.background }]}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.header}>
          <Text style={[styles.headerTitle, { color: colors.textPrimary }]}>
            Bonjour, {userName} !
          </Text>
          <Text style={[styles.headerSubtitle, { color: colors.textSecondary }]}>
            Voici l'etat de vos missions
          </Text>
        </View>

        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Ionicons name="play-circle" size={20} color={colors.primary} />
            <Text style={[styles.sectionTitle, { color: colors.textPrimary }]}>
              Missions en cours
            </Text>
            <View style={[styles.countBadge, { backgroundColor: colors.primary + '22' }]}>
              <Text style={[styles.countText, { color: colors.primary }]}>
                {inProgressMissions.length}
              </Text>
            </View>
          </View>

          {inProgressMissions.length === 0 ? (
            <View style={[styles.emptyCard, { backgroundColor: colors.cardBackground }]}>
              <Ionicons name="checkmark-circle" size={32} color={colors.success} />
              <Text style={[styles.emptyText, { color: colors.textSecondary }]}>
                Toutes vos missions sont terminees !
              </Text>
            </View>
          ) : (
            inProgressMissions.map(mission => {
              const progressColor = getProgressColor(mission.progress);
              return (
                <TouchableOpacity
                  key={mission.id}
                  style={[styles.missionCard, { backgroundColor: colors.cardBackground }]}
                  onPress={() => handleMissionPress(mission.id)}
                  activeOpacity={0.85}
                >
                  <View style={styles.missionCardTop}>
                    <View style={styles.missionInfo}>
                      <Text style={[styles.missionTitle, { color: colors.textPrimary }]} numberOfLines={1}>
                        {mission.title}
                      </Text>
                      {mission.subtitle && (
                        <Text style={[styles.missionSubtitle, { color: colors.textSecondary }]} numberOfLines={1}>
                          {mission.subtitle}
                        </Text>
                      )}
                    </View>
                    {mission.certification && (
                      <View style={[styles.certBadge, { backgroundColor: colors.primary + '22' }]}>
                        <Ionicons name="ribbon" size={12} color={colors.primary} />
                        <Text style={[styles.certBadgeText, { color: colors.primary }]}>
                          {mission.certification}
                        </Text>
                      </View>
                    )}
                  </View>
                  <View style={styles.progressRow}>
                    <View style={[styles.progressBar, { backgroundColor: colors.surfaceSecondary }]}>
                      <View style={[styles.progressFill, { width: `${mission.progress}%` as any, backgroundColor: progressColor }]} />
                    </View>
                    <Text style={[styles.progressLabel, { color: progressColor }]}>{mission.progress}%</Text>
                  </View>
                  <View style={styles.missionFooter}>
                    <View style={styles.metaRow}>
                      <Ionicons name="time-outline" size={12} color={colors.textTertiary} />
                      <Text style={[styles.metaText, { color: colors.textTertiary }]}>{mission.timeLeft}</Text>
                    </View>
                    <View style={styles.continueBtn}>
                      <Text style={[styles.continueBtnText, { color: colors.primary }]}>Continuer</Text>
                      <Ionicons name="arrow-forward" size={13} color={colors.primary} />
                    </View>
                  </View>
                </TouchableOpacity>
              );
            })
          )}
        </View>

        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Ionicons name="trophy" size={20} color={colors.success} />
            <Text style={[styles.sectionTitle, { color: colors.textPrimary }]}>
              Missions terminees
            </Text>
            <View style={[styles.countBadge, { backgroundColor: colors.success + '22' }]}>
              <Text style={[styles.countText, { color: colors.success }]}>
                {completedMissions.length}
              </Text>
            </View>
          </View>

          {completedMissions.length === 0 ? (
            <View style={[styles.emptyCard, { backgroundColor: colors.cardBackground }]}>
              <Ionicons name="rocket-outline" size={32} color={colors.textTertiary} />
              <Text style={[styles.emptyText, { color: colors.textSecondary }]}>
                Terminez une mission pour voir vos certificats ici.
              </Text>
            </View>
          ) : (
            completedMissions.map(mission => (
              <View key={mission.id} style={[styles.missionCard, { backgroundColor: colors.cardBackground }]}>
                <View style={styles.missionCardTop}>
                  <View style={styles.missionInfo}>
                    <Text style={[styles.missionTitle, { color: colors.textPrimary }]} numberOfLines={1}>
                      {mission.title}
                    </Text>
                    {mission.subtitle && (
                      <Text style={[styles.missionSubtitle, { color: colors.textSecondary }]} numberOfLines={1}>
                        {mission.subtitle}
                      </Text>
                    )}
                  </View>
                  <View style={[styles.completedBadge, { backgroundColor: colors.success + '22' }]}>
                    <Ionicons name="checkmark-circle" size={13} color={colors.success} />
                    <Text style={[styles.completedBadgeText, { color: colors.success }]}>Terminee</Text>
                  </View>
                </View>
                <View style={styles.progressRow}>
                  <View style={[styles.progressBar, { backgroundColor: colors.surfaceSecondary }]}>
                    <View style={[styles.progressFill, { width: '100%', backgroundColor: colors.success }]} />
                  </View>
                  <Text style={[styles.progressLabel, { color: colors.success }]}>100%</Text>
                </View>
                <TouchableOpacity
                  style={[styles.certButton, { backgroundColor: colors.success }]}
                  onPress={() => handleOpenCertificate(mission.id)}
                  activeOpacity={0.85}
                >
                  <Ionicons name="document-text" size={16} color="#FFFFFF" />
                  <Text style={styles.certButtonText}>Voir le certificat</Text>
                </TouchableOpacity>
              </View>
            ))
          )}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  content: { flex: 1, padding: 16 },
  header: { marginBottom: 24 },
  headerTitle: { fontSize: 24, fontWeight: 'bold', marginBottom: 4 },
  headerSubtitle: { fontSize: 15 },
  section: { marginBottom: 28 },
  sectionHeader: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 14 },
  sectionTitle: { fontSize: 18, fontWeight: '700', flex: 1 },
  countBadge: { paddingHorizontal: 9, paddingVertical: 3, borderRadius: 10 },
  countText: { fontSize: 13, fontWeight: '700' },
  emptyCard: { borderRadius: 12, padding: 24, alignItems: 'center', gap: 10, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.08, shadowRadius: 3, elevation: 2 },
  emptyText: { fontSize: 14, textAlign: 'center' },
  missionCard: { borderRadius: 12, padding: 16, marginBottom: 12, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, elevation: 3 },
  missionCardTop: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12, gap: 8 },
  missionInfo: { flex: 1 },
  missionTitle: { fontSize: 15, fontWeight: '700', marginBottom: 3 },
  missionSubtitle: { fontSize: 12, lineHeight: 16 },
  certBadge: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 8, paddingVertical: 4, borderRadius: 8, flexShrink: 0 },
  certBadgeText: { fontSize: 11, fontWeight: '700' },
  completedBadge: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 8, paddingVertical: 4, borderRadius: 8, flexShrink: 0 },
  completedBadgeText: { fontSize: 11, fontWeight: '700' },
  progressRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 12 },
  progressBar: { flex: 1, height: 8, borderRadius: 4, overflow: 'hidden' },
  progressFill: { height: '100%' as any, borderRadius: 4 },
  progressLabel: { fontSize: 12, fontWeight: '700', minWidth: 36, textAlign: 'right' },
  missionFooter: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  metaRow: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  metaText: { fontSize: 12 },
  continueBtn: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  continueBtnText: { fontSize: 13, fontWeight: '600' },
  certButton: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 10, paddingHorizontal: 16, borderRadius: 8 },
  certButtonText: { color: '#FFFFFF', fontSize: 14, fontWeight: '600' },
});
